import { useState, useCallback, useEffect } from "react";
import {
  validateEventReservationSettingsForm,
  validateEventReservationTimeSlotSettingsForm,
} from "../../validations/eventReservationSettingsForm";
import { useToastPromise } from "../../components/toastPromise";
import { CollaborationController } from "../../controllers/collaborationController";
import { Dropdown } from "../../components/dropdown";
import Button from "@mui/material/Button";
import "../../assets/css/setttings.css";
import { CollapsibleContainer } from "../../components/collapsibleContainer";
import { Textbox } from "../../components/textbox";
import { Checkbox } from "../../components/checkbox";
import { SlotStatusList } from "../../constants/slotStatusList";
import { TableList } from "../../components/tableList";
import { ReservationSettingsController } from "../../controllers/reservationSettingsController";
import { RichText } from "../../components/richText";
import { TableName } from "../../constants/tableName";
import { RichTextPreview } from "../../components/richTextPreview";
import { TableListPreview } from "../../components/tableListPreview";

export const EventReservationSettings = ({ user }) => {
  const toastPromise = useToastPromise();
  const emptyHTMLString = "<p>&nbsp;</p>";
  const initialEventTimeSlot = {
    id: "",
    slotTitle: "",
    slotStatus: "",
    maxPax: 0,
    startTime: "",
    endTime: "",
    eventDate: "",
    availableNormalTimeSlots: [],
  };
  const [eventTimeSlot, setEventTimeSlot] = useState(initialEventTimeSlot);
  const [timeSlotsList, setTimeSlotsList] = useState([]);
  const [eventErrors, setEventErrors] = useState({});
  const [eventIdCounter, setEventIdCounter] = useState(1);
  const initialEvent = {
    timeSlotsList: [],
    externalURL: "",
    maxPaxSelection: 0,
    reservationDescription: emptyHTMLString,
    allowNormalReservation: true,
  };
  const initialInputs = {
    id: "",
    collaborationId: "",
    eventSettings: initialEvent,
  };
  const [inputs, setInputs] = useState(initialInputs);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [collaborations, setCollaborations] = useState([]);
  const [
    isEventReservationDescriptionPreviewOpen,
    setIsEventReservationDescriptionPreviewOpen,
  ] = useState(false);
  const [
    isEventReservationDescriptionPreviewDisabled,
    setIsEventReservationDescriptionPreviewDisabled,
  ] = useState(true);
  const [availableNormalTimeSlots, setAvailableNormalTimeSlots] = useState([]);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [
    selectedAvailableNormalTimeSlots,
    setSelectedAvailableNormalTimeSlots,
  ] = useState([]);
  const getLargestId = (slots) => {
    return slots.reduce((max, slot) => Math.max(max, slot.id), 0);
  };
  const resetForm = () => {
    setInputs(initialInputs);
    setErrors({});
    setEventTimeSlot(initialEventTimeSlot);
    setTimeSlotsList([]);
    setEventErrors({});
    setEventIdCounter(1);
    setIsEventReservationDescriptionPreviewDisabled(true);
    setAvailableNormalTimeSlots([]);
  };
  const handleSubmit = (event) => {
    event.preventDefault();
    const newErrors = validateEventReservationSettingsForm(inputs);
    setErrors(newErrors);
    if (Object.keys(newErrors).length !== 0) {
      console.debug("Form submission failed due to validation errors.");
      return;
    }
    console.debug("Form validated successfully.");
    saveReservationSettings();
  };
  const handleSubmitEvent = (event) => {
    event.preventDefault();
    const newErrors =
      validateEventReservationTimeSlotSettingsForm(eventTimeSlot);
    setEventErrors(newErrors);
    if (Object.keys(newErrors).length !== 0) {
      console.debug("Form submission failed due to validation errors.");
      return;
    }
    console.debug("Form validated successfully.");
    saveReservationSettingsEvent();
  };
  const saveReservationSettings = () => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        inputs.id
          ? ReservationSettingsController.update({
              parameters: {
                ...inputs,
                id: inputs.id,
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            })
          : ReservationSettingsController.add({
              parameters: {
                ...inputs,
                createdBy: user.uid,
                createdDate: new Date().toLocaleString(),
                lastModifiedBy: user.uid,
                lastModifiedDate: new Date().toLocaleString(),
              },
              resolve: resolve,
              reject: reject,
            });
      }),
      successMessage: (data) => {
        console.debug(data);
        return data;
      },
      errorMessage: (data) => data,
    }).finally(() => {
      setLoading(false);
      resetForm();
      getAll();
    });
  };
  const saveReservationSettingsEvent = () => {
    const availableSlots =
      (inputs?.eventSettings?.allowNormalReservation ?? true)
        ? saveAvailableNormalReservation(eventTimeSlot)
        : [];
    setTimeSlotsList((prevTimeSlots) => [
      ...prevTimeSlots,
      {
        ...eventTimeSlot,
        id: eventIdCounter,
        availableNormalTimeSlots: availableSlots,
      },
    ]);
    setEventTimeSlot({ ...initialEventTimeSlot, id: eventIdCounter + 1 });
    setEventErrors({});
    setEventIdCounter(eventIdCounter + 1);
  };
  const saveAvailableNormalReservation = (eventTimeSlot) => {
    let slots = inputs?.normalSettings?.timeSlotsList;
    if (timeSlotsList.length > 0) {
      const oldTimeSlotsList = timeSlotsList.find(
        (timeSlot) => timeSlot.eventDate === eventTimeSlot.eventDate
      );
      if (oldTimeSlotsList && oldTimeSlotsList.availableNormalTimeSlots)
        slots = oldTimeSlotsList.availableNormalTimeSlots;
    }
    const eventDate = eventTimeSlot.eventDate;
    const startTime = parseTime(eventDate, eventTimeSlot.startTime);
    const endTime = parseTime(eventDate, eventTimeSlot.endTime);
    const startTimeMinus1Hr = new Date(startTime.getTime() - 60 * 60 * 1000);
    const endTimePlus1Hr = new Date(endTime.getTime() + 60 * 60 * 1000);
    const availableSlots =
      slots?.filter((slot) => {
        const slotStartTime = parseTime(eventDate, slot.startTime);
        const slotEndTime = parseTime(eventDate, slot.endTime);
        return (
          slotStartTime >= endTimePlus1Hr || slotEndTime <= startTimeMinus1Hr
        );
      }) || [];
    setAvailableNormalTimeSlots(availableSlots);
    setTimeSlotsList((prevTimeSlots) =>
      prevTimeSlots.map((timeSlot) =>
        timeSlot.eventDate === eventTimeSlot.eventDate
          ? { ...timeSlot, availableNormalTimeSlots: availableSlots }
          : timeSlot
      )
    );
    return availableSlots;
  };
  const parseTime = (dateString, timeString) => {
    const [hour, minute] = timeString.split(":").map(Number);
    const date = new Date(dateString);
    date.setHours(hour, minute, 0, 0);
    return date;
  };
  const handleTextChange = (event) => {
    saveInputs(event.target.name, event.target.value);
  };
  const handleRichTextChange = (event, editor) => {
    const data = editor.getData();
    saveInputs(editor.config.get("name"), data);
    if (editor.config.get("name") === "reservationDescription")
      setIsEventReservationDescriptionPreviewDisabled(
        data?.trim() === emptyHTMLString
      );
  };
  const saveInputs = (name, value) => {
    setInputs((prevInputs) => ({
      ...prevInputs,
      eventSettings: {
        ...prevInputs.eventSettings,
        [name]: value,
      },
    }));
  };
  const onHandleEventChange = useCallback((value, name) => {
    saveEventInputs(name, value);
  }, []);
  const handleEventTextChange = (event) => {
    saveEventInputs(event.target.name, event.target.value);
  };
  const saveEventInputs = (name, value) => {
    setEventTimeSlot((values) => ({ ...values, [name]: value }));
  };
  const handleCheckboxChange = (event) => {
    saveInputs(event.target.name, event.target.checked);
  };
  const handlePreviewPopupDialogOpen = (availableNormalTimeSlots = null) => {
    setSelectedAvailableNormalTimeSlots(availableNormalTimeSlots);
    setIsPreviewOpen(true);
  };
  const handlePreviewClose = () => {
    setIsPreviewOpen(false);
  };
  const getAllCollaborations = () => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        CollaborationController.getAll({
          resolve: resolve,
          reject: reject,
        });
      }),
      successMessage: (data) => {
        setCollaborations(
          data.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }))
        );
        return "Collaboration details retrieved successfully.";
      },
      errorMessage: (data) => data,
    }).finally(() => setLoading(false));
  };
  const getAll = () => {
    setLoading(true);
    toastPromise({
      promise: new Promise((resolve, reject) => {
        ReservationSettingsController.getAll({
          resolve: resolve,
          reject: reject,
        });
      }),
      successMessage: (data) => {
        if (data) {
          const fetchedData = data.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          if (fetchedData.length > 0) {
            setInputs(fetchedData[0]);
            const timeSlotsList =
              fetchedData[0]?.eventSettings?.timeSlotsList || [];
            if (timeSlotsList.length > 0) {
              setTimeSlotsList(timeSlotsList);
              setEventIdCounter(getLargestId(timeSlotsList) + 1);
            }
            setIsEventReservationDescriptionPreviewDisabled(
              fetchedData[0]?.eventSettings?.reservationDescription === "" ||
                fetchedData[0]?.eventSettings?.reservationDescription?.trim() ===
                  emptyHTMLString
            );
          }
        }
        return "Reservation settings details retrieved successfully.";
      },
      errorMessage: (data) => data,
    }).finally(() => setLoading(false));
  };
  useEffect(() => {
    getAllCollaborations();
    getAll();
  }, []);
  useEffect(() => {
    setInputs((prevInputs) => ({
      ...prevInputs,
      eventSettings: {
        ...prevInputs.eventSettings,
        timeSlotsList: timeSlotsList,
      },
    }));
  }, [timeSlotsList]);

  return (
    <section id="eventReservationSettings">
      <h4>EVENT RESERVATION SETTINGS</h4>
      <hr />
      <Button
        onClick={handleSubmit}
        disabled={loading || inputs?.eventSettings?.timeSlotsList?.length < 1}
      >
        {loading ? "Saving" : "Save"}
      </Button>
      <form onSubmit={handleSubmit}>
        <Textbox
          type="text"
          name="reservationSettingsId"
          placeholder="Reservation Settings ID"
          value={inputs?.id ?? "NA"}
          readOnly={true}
        />
        <Textbox
          type="text"
          name="collaborationId"
          placeholder="Collaboration ID"
          value={
            collaborations.find((data) => data.id === inputs?.collaborationId)
              ?.id ?? "NA"
          }
          readOnly={true}
        />
        <Textbox
          type="text"
          name="collaborationName"
          placeholder="Collaboration Name"
          value={
            collaborations.find((data) => data.id === inputs?.collaborationId)
              ?.title ?? "NA"
          }
          readOnly={true}
        />
        <Textbox
          type="text"
          name="startDate"
          placeholder="Start Date"
          value={
            collaborations.find((data) => data.id === inputs?.collaborationId)
              ?.startDate ?? "NA"
          }
          readOnly={true}
        />
        <Textbox
          type="text"
          name="endDate"
          placeholder="End Date"
          value={
            collaborations.find((data) => data.id === inputs?.collaborationId)
              ?.endDate ?? "NA"
          }
          readOnly={true}
        />
        <Textbox
          type="text"
          name="externalURL"
          placeholder="External URL"
          value={inputs?.eventSettings?.externalURL}
          onChange={handleTextChange}
          errorMsg={errors.externalURL}
          isMandatory={false}
        />
        <Textbox
          type="number"
          name="maxPaxSelection"
          placeholder="Max Pax Selection"
          value={inputs?.eventSettings?.maxPaxSelection}
          onChange={handleTextChange}
          errorMsg={errors.maxPaxSelection}
        />
        <RichText
          name="reservationDescription"
          onChange={handleRichTextChange}
          placeholder="Reservation Description"
          data={
            inputs?.eventSettings?.reservationDescription || emptyHTMLString
          }
          errorMsg={errors.reservationDescription}
          tableName={TableName.RESERVATIONSETTINGS}
        />
        <Button
          onClick={() => setIsEventReservationDescriptionPreviewOpen(true)}
          disabled={isEventReservationDescriptionPreviewDisabled}
        >
          Preview
        </Button>
        <RichTextPreview
          isPreviewOpen={isEventReservationDescriptionPreviewOpen}
          handlePreviewClose={() =>
            setIsEventReservationDescriptionPreviewOpen(false)
          }
          content={inputs?.eventSettings?.reservationDescription}
        />
        <hr />
        <div>
          <CollapsibleContainer
            title="Event Slots"
            setCollapsed={timeSlotsList?.length > 0}
            element={
              <div>
                <Button onClick={handleSubmitEvent} disabled={loading}>
                  {loading ? "Adding" : "Add"}
                </Button>
                <Textbox
                  type="text"
                  name="slotTitle"
                  placeholder="Slot Title"
                  value={eventTimeSlot.slotTitle}
                  onChange={handleEventTextChange}
                  errorMsg={eventErrors.slotTitle}
                />
                <Dropdown
                  name="slotStatus"
                  title="Slot Status"
                  value={eventTimeSlot?.slotStatus}
                  options={SlotStatusList}
                  onChangeFunc={onHandleEventChange}
                  isClearable={false}
                  placeholder="Slot Status"
                  errorMsg={eventErrors.slotStatus}
                />
                <Textbox
                  type="number"
                  name="maxPax"
                  placeholder="Max Pax"
                  value={eventTimeSlot.maxPax}
                  onChange={handleEventTextChange}
                  errorMsg={eventErrors.maxPax}
                />
                <Textbox
                  type="time"
                  name="startTime"
                  placeholder="Start Time"
                  value={eventTimeSlot.startTime}
                  onChange={handleEventTextChange}
                  errorMsg={eventErrors.startTime}
                />
                <Textbox
                  type="time"
                  name="endTime"
                  placeholder="End Time"
                  value={eventTimeSlot.endTime}
                  onChange={handleEventTextChange}
                  errorMsg={eventErrors.endTime}
                />
                <Textbox
                  type="date"
                  name="eventDate"
                  placeholder="Event Date"
                  value={eventTimeSlot.eventDate}
                  onChange={handleEventTextChange}
                  errorMsg={eventErrors.eventDate}
                />
              </div>
            }
          />
          {timeSlotsList?.length > 0 && (
            <TableList
              loading={loading}
              list={timeSlotsList}
              isPreview={true}
              handlePreviewPopupDialogOpen={handlePreviewPopupDialogOpen}
              deletePromise={(selectedRows, success) => {
                setTimeSlotsList((prevList) =>
                  prevList.filter((item) => !selectedRows.includes(item.id))
                );
              }}
            />
          )}
          <TableListPreview
            loading={loading}
            isPreviewOpen={isPreviewOpen}
            handlePreviewClose={handlePreviewClose}
            content={selectedAvailableNormalTimeSlots}
          />
        </div>
        <hr />
        <Checkbox
          name="allowNormalReservation"
          placeholder="Allow Normal Reservation"
          checked={inputs?.eventSettings?.allowNormalReservation ?? true}
          onChange={(e) => handleCheckboxChange(e)}
        />
        {(inputs?.eventSettings?.allowNormalReservation ?? true) && (
          <div>
            <CollapsibleContainer
              title="Available Normal Slots"
              setCollapsed={timeSlotsList?.length > 0}
              element={
                <div>
                  {availableNormalTimeSlots?.length > 0 && (
                    <TableList
                      loading={loading}
                      list={availableNormalTimeSlots}
                      isPreview={true}
                      isEditable={false}
                      deletePromise={() => {}}
                    />
                  )}
                </div>
              }
            />
          </div>
        )}
      </form>
    </section>
  );
};
